import { useEffect, useRef } from 'react';

// Configuration options
const CONFIG = {
  PARTICLE_COUNT: 100, // Number of particles
  PARTICLE_SIZE: { MIN: 2, MAX: 5 }, // Size in px
  SPEED: { MIN: 0.1, MAX: 0.4 }, // Pixels per frame
  COLORS: ['#8B5CF6', '#06B6D4'], // Purple and Cyan
  OPACITY: { MIN: 0.2, MAX: 0.9 },
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  opacity: number;
}

const randomBetween = (min: number, max: number) =>
  Math.random() * (max - min) + min;

const ParticlesCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const particlesRef = useRef<Particle[]>([]);
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to fill parent
    const resize = () => {
      if (!canvas.parentElement) return;
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    const rect = canvas.parentElement
      ? canvas.parentElement.getBoundingClientRect()
      : { width: 0, height: 0 };
    const particles: Particle[] = Array.from(
      { length: CONFIG.PARTICLE_COUNT },
      () => ({
        x: randomBetween(0, rect.width * dpr),
        y: randomBetween(0, rect.height * dpr),
        vx: randomBetween(-CONFIG.SPEED.MAX, CONFIG.SPEED.MAX),
        vy: randomBetween(-CONFIG.SPEED.MAX, CONFIG.SPEED.MAX),
        size:
          randomBetween(CONFIG.PARTICLE_SIZE.MIN, CONFIG.PARTICLE_SIZE.MAX) *
          dpr,
        color: CONFIG.COLORS[Math.floor(Math.random() * CONFIG.COLORS.length)],
        opacity: randomBetween(CONFIG.OPACITY.MIN, CONFIG.OPACITY.MAX),
      })
    );
    particlesRef.current = particles;

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particlesRef.current) {
        // Move
        p.x += p.vx * dpr;
        p.y += p.vy * dpr;
        // Bounce off edges
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        // Draw
        ctx.globalAlpha = p.opacity;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8 * dpr;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full z-10 pointer-events-none"
      style={{ display: 'block' }}
    />
  );
};

export default ParticlesCanvas;
